import { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Swords, Timer } from 'lucide-react';

import { useActiveMatchContext } from '@/contexts/ActiveMatchContext';
import { useCountdown } from '@/hooks/useCountdown';
import { useHaptics } from '@/hooks/useHaptics';
import { useLivelloIndietro } from '@/hooks/useLivelloIndietro';
import { destinazioneDi, type LimuloAzionePendente } from '@/lib/limulo/azionePersistente';
import {
  chiaveModaleCheckIn,
  modaleCheckInAncoraAperta,
  modaleCheckInDaAprire,
  modaliCheckInConsumate,
} from '@/lib/limulo/modaleCheckIn';
import { presentazioneDi, titoloAzioneOf } from '@/lib/limulo/presentazione';
import { gameNameOf } from '@/lib/limulo/profiles';
import { cn } from '@/lib/cn';
import { useGetLimuloGamesQuery, useGetLimuloTournamentQuery } from '@/services/limuloApi';

/**
 * 🔴 LA MODALE DEL CHECK-IN D'INCONTRO — Luca, 2026-09-26T11:20:53Z, testuale: «quando la partita viene
 * trovata e si deve fare checkin al match, fai venire fuori modale più impattante per richiamare
 * attenzione con al countdown».
 *
 * Montata SOLO accanto alla barra delle azioni del motore (`AzioniDalMotore`): stesso flag
 * (`VITE_FEATURE_TOURNAMENT_ACTIONS_ENABLED`), stesse `azioni`, nessuna lettura delle azioni sua — il
 * tempo reale (`Tournament.action.changed`) rilegge la stessa query, e la modale lo segue. Quando si apre
 * e quando si chiude lo decide `lib/limulo/modaleCheckIn.ts`; qui c'e' il disegno.
 *  · il bottone principale porta DOVE porta «Vai» della barra (`destinazioneDi`: la scheda d'incontro,
 *    dove c'e' «Sono pronto»). ⛔ Niente check-in da qui: nessuna chiamata di scrittura;
 *  · «Più tardi», il velo, Esc e il tasto indietro di Telegram (la pila dei livelli,
 *    `lib/livelliIndietro.ts`) chiudono e lasciano la barra, senza cambiare schermata; un cambio di
 *    rotta la chiude;
 *  · all'apertura il fuoco va su «Vai al check-in», alla chiusura torna dov'era;
 *  · il conto alla rovescia e' `useCountdown` sulla STESSA scadenza del server che legge la barra: un
 *    solo orologio. A zero la modale resta («Tempo scaduto») finche' il server non spegne l'azione.
 */
export function ModaleCheckIn({ azioni }: { azioni: readonly LimuloAzionePendente[] }) {
  const location = useLocation();
  const { descriptor } = useActiveMatchContext();
  const haptics = useHaptics();
  const [aperta, setAperta] = useState<{ chiave: string; altri: number; rotta: string } | null>(null);
  const partitaViva = descriptor != null;

  useEffect(() => {
    if (aperta !== null) return;
    const esito = modaleCheckInDaAprire({
      azioni,
      consumate: modaliCheckInConsumate,
      pathname: location.pathname,
      partitaViva,
    });
    for (const k of esito.consuma) modaliCheckInConsumate.add(k);
    if (esito.mostra === null) return;
    setAperta({ chiave: chiaveModaleCheckIn(esito.mostra), altri: esito.altri, rotta: location.pathname });
    haptics.notification('warning');
  }, [azioni, location.pathname, partitaViva, aperta, haptics]);

  const azione =
    aperta === null
      ? null
      : modaleCheckInAncoraAperta({ chiave: aperta.chiave, azioni, pathname: location.pathname, partitaViva });
  const daChiudere = aperta !== null && (azione === null || location.pathname !== aperta.rotta);
  useEffect(() => {
    if (daChiudere) setAperta(null);
  }, [daChiudere]);

  if (aperta === null || azione === null || daChiudere) return null;
  return <Modale azione={azione} altri={aperta.altri} onChiudi={() => setAperta(null)} />;
}

function Modale({
  azione,
  altri,
  onChiudi,
}: {
  azione: LimuloAzionePendente;
  altri: number;
  onChiudi: () => void;
}) {
  const { t } = useTranslation('limulo');
  const navigate = useNavigate();
  const haptics = useHaptics();
  const vaiRef = useRef<HTMLButtonElement | null>(null);
  const modaleRef = useRef<HTMLDivElement | null>(null);
  // Il gioco lo porta la scheda torneo (la voce del motore no): una lettura, senza polling, gia' in
  // cache se la scheda e' stata aperta. Ignoto ⇒ la riga non c'e'.
  const { data: torneo } = useGetLimuloTournamentQuery(azione.tournamentId);
  const { data: giochi } = useGetLimuloGamesQuery(undefined);
  const gioco = torneo?.gameId ? gameNameOf(torneo.gameId, giochi) : null;
  // Lo STESSO orologio della barra: stesso hook, stessa scadenza del server (ADR-0158: e' solo testo).
  const manca = useCountdown(azione.scadenza);

  useLivelloIndietro(true, onChiudi);

  // Il fuoco sul gesto principale all'apertura, e restituito alla chiusura — ma solo se nessun altro
  // l'ha preso intanto, e senza far scorrere la pagina (revisione di #434, seconda passata). «Nessun
  // altro» = il fuoco e' sul corpo (la modale smontata) oppure ancora DENTRO la modale: e' il caso di
  // StrictMode, che esegue la pulizia senza togliere il DOM, col fuoco su «Vai» — senza questo ramo
  // la riesecuzione catturava «Vai» come `prima` e in sviluppo il fuoco non tornava piu' (revisione
  // di #441, 5).
  useEffect(() => {
    const prima = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    const nodo = modaleRef.current;
    vaiRef.current?.focus();
    return () => {
      const ora = document.activeElement;
      if (prima?.isConnected && (ora === null || ora === document.body || (nodo?.contains(ora) ?? false))) {
        prima.focus({ preventScroll: true });
      }
    };
  }, []);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onChiudi();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onChiudi]);

  // r15: una partita di coda non dice «Round» (v. `titoloAzioneOf`); finche' il torneo non e' letto, il titolo e' quello di oggi.
  // L'`origine` della voce (qlash-limulo#216) decide la presentazione senza aspettare la scheda; la scheda resta letta per il
  // GIOCO (sopra), che la voce non porta, e fa da ripiego quando la voce non ha l'`origine`.
  const presentazione =
    typeof azione.origine === 'string'
      ? presentazioneDi(azione.tournamentId, { origine: azione.origine })
      : torneo
        ? presentazioneDi(azione.tournamentId, torneo)
        : null;
  const titolo = titoloAzioneOf(azione, presentazione, (torneoNome, round) =>
    t('persistente.titolo', { torneo: torneoNome, round }),
  );

  return (
    <div
      className="fixed inset-0 z-[70] flex items-center justify-center px-4 py-4"
      ref={modaleRef}
      role="alertdialog"
      aria-modal
      aria-labelledby="modale-checkin-titolo"
      aria-describedby="modale-checkin-spiega"
      data-testid="modale-checkin"
    >
      <button
        type="button"
        aria-label={t('persistente.modale.dopo')}
        data-testid="modale-checkin-velo"
        onClick={onChiudi}
        className="absolute inset-0 bg-black/75"
      />
      {/* `max-h-full overflow-y-auto`: su un viewport basso (orizzontale, telefono piccolo) la scheda
          scorre invece di finire tagliata con «Vai» fuori dallo schermo (revisione di #434). */}
      <div className="relative max-h-full w-full max-w-sm overflow-y-auto rounded-3xl border-2 border-warning/60 bg-bg-secondary text-center shadow-[0_20px_60px_rgba(0,0,0,0.6)]">
        <div className="bg-warning/15 px-5 pb-4 pt-5">
          <span className="mx-auto grid h-14 w-14 place-items-center rounded-2xl bg-warning/25 text-warning">
            <Swords aria-hidden className="h-8 w-8" strokeWidth={2.4} />
          </span>
          <p
            id="modale-checkin-titolo"
            className="mt-3 text-xs font-extrabold uppercase tracking-[0.18em] text-warning"
          >
            {t('persistente.tipo.checkin-incontro')}
          </p>
          {gioco ? (
            <p data-testid="modale-checkin-gioco" className="mt-1 text-sm font-semibold text-fg-muted">
              {gioco}
            </p>
          ) : null}
          <p className="mt-1 text-lg font-extrabold leading-tight text-fg">{titolo}</p>
          {azione.avversario ? (
            <p data-testid="modale-checkin-avversario" className="mt-1 text-sm font-bold text-fg">
              {t('persistente.modale.contro', { nome: azione.avversario })}
            </p>
          ) : null}
        </div>

        {azione.scadenza !== null ? (
          <div className="px-5 pt-5">
            {/* `word-spacing`: in maiuscolo a 12 px lo spazio di Manrope e' 2,6 px, e «IL CHECK-IN» nella cattura
                a 390 px si leggeva «ILCHECK-IN» (lo spazio c'era: misurato carattere per carattere). */}
            <p className="flex items-center justify-center gap-1.5 text-xs font-bold uppercase tracking-wider text-fg-muted [word-spacing:0.25em]">
              <Timer aria-hidden className="h-4 w-4 shrink-0" />
              {manca !== null ? t('persistente.modale.tempo') : t('persistente.modale.scaduto')}
            </p>
            <p
              data-testid="modale-checkin-countdown"
              data-scaduto={manca === null ? 'true' : undefined}
              aria-live="off"
              className={cn(
                'mt-1 font-display text-6xl font-extrabold tabular-nums leading-none',
                manca !== null ? 'text-warning' : 'text-fg-muted',
              )}
            >
              {manca ?? '0:00'}
            </p>
          </div>
        ) : null}

        <p id="modale-checkin-spiega" className="px-6 pt-4 text-sm leading-snug text-fg-muted">
          {t('persistente.modale.spiega')}
        </p>
        {altri > 0 ? (
          <p data-testid="modale-checkin-altri" className="px-6 pt-2 text-xs font-semibold text-warning">
            {t('persistente.modale.altri', { n: altri })}
          </p>
        ) : null}

        <div className="flex flex-col gap-2 px-5 pb-5 pt-5">
          <button
            type="button"
            ref={vaiRef}
            data-testid="modale-checkin-vai"
            onClick={() => {
              haptics.impact('medium');
              // ⛔ Niente `onChiudi()` qui (revisione di #434, 🔴): chiudere PRIMA di navigare faceva girare
              // l'apertura sulla rotta VECCHIA, che consumava un secondo check-in arrivato intanto e vibrava
              // per una modale subito richiusa dal cambio di rotta. La chiude il cambio di rotta stesso: la
              // destinazione non e' mai la rotta attuale (sulla propria scheda la modale non compare).
              const dove = destinazioneDi(azione);
              if (dove !== null) navigate(dove);
            }}
            className="w-full rounded-2xl bg-brand py-3.5 text-base font-extrabold text-brand-fg transition-opacity active:opacity-80"
          >
            {t('persistente.modale.vai')}
          </button>
          <button
            type="button"
            data-testid="modale-checkin-dopo"
            onClick={() => {
              haptics.selection();
              onChiudi();
            }}
            className="w-full rounded-2xl py-2.5 text-sm font-bold text-fg-muted transition-opacity active:opacity-70"
          >
            {t('persistente.modale.dopo')}
          </button>
        </div>
      </div>
    </div>
  );
}
